import React from 'react';
import DividerWithTitle from '../../components/DividerWithTitle';
import { Box, Typography } from '@mui/material';
import {
  FeaturesContainer,
  FeaturesSection,
  FeatureSubtitle,
} from './components/FeatureComponents';
import { styled } from '@mui/material/styles';
import AssociateBadge from '../../assets/imgs/microsoft-certified-associate-badge.svg';
import ExpertBadge from '../../assets/imgs/microsoft-certified-expert-badge.svg';
import GeneralBadge from '../../assets/imgs/microsoft-certified-general-badge.svg';
import SpecialtyBadge from '../../assets/imgs/microsoft-certified-specialty-badge.svg';
import itilBadge from '../../assets/imgs/itil-foundation.jpg';
import { useTranslation } from 'react-i18next';

const CertificationsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: '650px',
  alignItems: 'center',
  justifyItems: 'center',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
  },
}));

const SvgBadgeWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '180px',
  filter: 'grayscale(100%)',
  transition: 'all 0.4s ease-in-out',
  cursor: 'pointer',
  '& svg': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  '&:hover': {
    filter: 'grayscale(0%)',
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '140px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '110px',
  },
}));

const CertificationBadge = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxWidth: '180px',
  filter: 'grayscale(100%)',
  transition: 'all 0.4s ease-in-out',
  cursor: 'pointer',
  objectFit: 'contain',
  borderRadius: '8px',
  '&:hover': {
    filter: 'grayscale(0%)',
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '140px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '110px',
  },
}));

const ListText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const ListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(1),
  marginTop: theme.spacing(2),
  maxWidth: '420px',
}));

export default function LandingCertificationsStandards() {
  const { t } = useTranslation();

  const standards = t('informationServices.certifications.standards', {
    returnObjects: true,
  }) as string[];

  return (
    <FeaturesSection id='standards'>
      <DividerWithTitle
        title={t('informationServices.certifications.dividerTitle')}
      />
      <FeaturesContainer>
        <Box
          display='flex'
          flexDirection='column'
          rowGap={10}
          sx={{
            width: { xs: '100%', md: '420px' },
            rowGap: { xs: 4, md: 10 },
          }}
        >
          <FeatureSubtitle>
            {t('informationServices.certifications.subtitle')}
          </FeatureSubtitle>
          <ListContainer>
            {standards.map((item, index) => (
              <ListText key={index}>{item}</ListText>
            ))}
          </ListContainer>
        </Box>
        <Box
          display='flex'
          flexDirection='column'
          rowGap={4}
          sx={{
            width: { xs: '100%', md: '650px' },
          }}
        >
          <Typography
            fontWeight={300}
            sx={{
              fontSize: { xs: '18px', md: '24px' },
            }}
          >
            {t('informationServices.certifications.description')}
          </Typography>
          <CertificationsGrid>
            <SvgBadgeWrapper title='Microsoft Certified Associate'>
              <AssociateBadge />
            </SvgBadgeWrapper>
            <SvgBadgeWrapper title='Microsoft Certified Expert'>
              <ExpertBadge />
            </SvgBadgeWrapper>
            <SvgBadgeWrapper title='Microsoft Certified'>
              <GeneralBadge />
            </SvgBadgeWrapper>
            <SvgBadgeWrapper title='Microsoft Certified Specialty'>
              <SpecialtyBadge />
            </SvgBadgeWrapper>
            <CertificationBadge src={itilBadge} alt='ITIL Foundation' />
          </CertificationsGrid>
        </Box>
      </FeaturesContainer>
    </FeaturesSection>
  );
}
